import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Chip,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  DirectionsCar as CarIcon,
  WhatsApp as WhatsAppIcon,
  Message as MessageIcon,
  PeopleAlt as UsersIcon,
  Archive as ArchiveIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  SupportAgent as SupportIcon,
  Contacts as LeadsIcon,
  PrivacyTip as PrivacyIcon,
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Circle as CircleIcon,
  Analytics as AnalyticsIcon,
  ManageAccounts as ManageAccountsIcon,
  Tune as TuneIcon,
  Speed as SpeedIcon,
  Layers as LayersIcon,
  CreditCard as CreditCardIcon,
  ReceiptLong as ReceiptLongIcon,
  SmartToy as SmartToyIcon,
  FilterAlt as FilterAltIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';

interface NavLeaf {
  label: string;
  path: string;
  icon: React.ReactNode;
  roles?: string[];
  badge?: 'rides';
}

interface NavGroup {
  label: string;
  icon: React.ReactNode;
  roles?: string[];
  children: NavLeaf[];
}

type NavEntry = NavLeaf | NavGroup;

function isGroup(entry: NavEntry): entry is NavGroup {
  return 'children' in entry;
}

const ALL_NAV_ENTRIES: NavEntry[] = [
  { label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon fontSize="small" /> },
  { label: 'Rides', path: '/rides', icon: <CarIcon fontSize="small" />, badge: 'rides', roles: ['admin', 'manager'] },
  {
    label: 'WhatsApp',
    icon: <WhatsAppIcon fontSize="small" />,
    roles: ['admin', 'manager'],
    children: [
      { label: 'Overview', path: '/whatsapp', icon: <AnalyticsIcon fontSize="small" /> },
      { label: 'Accounts', path: '/whatsapp/accounts', icon: <ManageAccountsIcon fontSize="small" /> },
      { label: 'Live Messages', path: '/whatsapp/live', icon: <MessageIcon fontSize="small" /> },
      { label: 'Messages', path: '/whatsapp/messages', icon: <ArchiveIcon fontSize="small" /> },
      { label: 'Leads', path: '/whatsapp/leads', icon: <LeadsIcon fontSize="small" /> },
    ],
  },
  { label: 'Drivers', path: '/drivers', icon: <UsersIcon fontSize="small" />, roles: ['admin'] },
  {
    label: 'Operations',
    icon: <TuneIcon fontSize="small" />,
    roles: ['admin'],
    children: [
      { label: 'Overview', path: '/operations', icon: <SpeedIcon fontSize="small" /> },
      { label: 'Plans & TopUps', path: '/operations/plans', icon: <LayersIcon fontSize="small" /> },
      { label: 'Payment Gateways', path: '/operations/payments', icon: <CreditCardIcon fontSize="small" /> },
      { label: 'Billing & Refunds', path: '/operations/billing', icon: <ReceiptLongIcon fontSize="small" /> },
      { label: 'AI Controls', path: '/operations/ai', icon: <SmartToyIcon fontSize="small" /> },
      { label: 'Pre Filters', path: '/operations/prefilters', icon: <FilterAltIcon fontSize="small" /> },
    ],
  },
  { label: 'Support Tickets', path: '/support-tickets', icon: <SupportIcon fontSize="small" />, roles: ['admin', 'manager'] },
  { label: 'Data Requests', path: '/data-deletion-requests', icon: <PrivacyIcon fontSize="small" />, roles: ['admin'] },
  { label: 'Settings', path: '/settings', icon: <SettingsIcon fontSize="small" /> },
];

const SIDEBAR_WIDTH = 240;

function getPageTitle(pathname: string): string {
  for (const entry of ALL_NAV_ENTRIES) {
    if (isGroup(entry)) {
      for (const child of entry.children) {
        if (pathname === child.path || pathname.startsWith(child.path + '/')) return child.label;
      }
    } else if (pathname === entry.path || pathname.startsWith(entry.path + '/')) {
      return entry.label;
    }
  }
  return 'Admin Panel';
}

const SidebarContent: React.FC<{ onNavigate?: () => void }> = ({ onNavigate }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { connected, whatsappStatus, newRideCount } = useSocket();

  const isAdmin = user?.role === 'admin';
  const canOperate = user?.role === 'admin' || user?.role === 'manager';

  const visibleEntries = useMemo(() => {
    return ALL_NAV_ENTRIES.filter((entry) => {
      if (!entry.roles) return true;
      if (entry.roles.includes('admin') && isAdmin) return true;
      if (entry.roles.includes('manager') && canOperate) return true;
      return false;
    }).map((entry) => {
      if (!isGroup(entry)) return entry;
      return {
        ...entry,
        children: entry.children.filter((child) => {
          if (!child.roles) return true;
          if (child.roles.includes('admin') && isAdmin) return true;
          if (child.roles.includes('manager') && canOperate) return true;
          return false;
        }),
      } as NavGroup;
    });
  }, [isAdmin, canOperate]);

  // Auto-expand the group that contains the active route
  const initialOpenGroups = useMemo(() => {
    const open = new Set<string>();
    for (const entry of visibleEntries) {
      if (isGroup(entry)) {
        const hasActive = entry.children.some(
          (c) => location.pathname === c.path || location.pathname.startsWith(c.path + '/')
        );
        if (hasActive) open.add(entry.label);
      }
    }
    return open;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [openGroups, setOpenGroups] = useState<Set<string>>(initialOpenGroups);

  const toggleGroup = (label: string) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const handleNav = (path: string) => {
    navigate(path);
    onNavigate?.();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    onNavigate?.();
  };

  const isLeafActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const isGroupActive = (group: NavGroup) =>
    group.children.some((c) => isLeafActive(c.path));

  const itemSx = (active: boolean) => ({
    borderRadius: 1.5,
    mb: 0.25,
    py: 0.9,
    color: active ? '#07182d' : 'rgba(255,255,255,0.82)',
    bgcolor: active ? '#ffc928 !important' : 'transparent',
    '&:hover': {
      bgcolor: active ? '#ffc928' : 'rgba(255,255,255,0.07)',
      color: active ? '#07182d' : 'white',
    },
    '&.Mui-selected': { bgcolor: '#ffc928' },
  });

  return (
    <Stack sx={{ height: '100%', overflow: 'hidden' }}>
      {/* Logo */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
        <Box
          component="img"
          src="/assets/ride-saathi-monogram.png"
          alt="RideSaathi"
          sx={{ height: 32, width: 'auto', filter: 'brightness(0) invert(1)' }}
        />
        <Typography variant="subtitle1" fontWeight={800} color="white" noWrap>
          Admin Panel
        </Typography>
      </Box>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', flexShrink: 0 }} />

      {/* Status chips */}
      <Stack direction="row" spacing={0.75} sx={{ px: 1.5, py: 1, flexWrap: 'wrap', rowGap: 0.5, flexShrink: 0 }}>
        <Chip
          icon={<CircleIcon sx={{ fontSize: '10px !important' }} />}
          label={connected ? 'Live' : 'Offline'}
          size="small"
          color={connected ? 'success' : 'error'}
          sx={{ fontSize: 11, height: 22, '& .MuiChip-icon': { ml: 0.5 } }}
        />
        <Chip
          icon={<WhatsAppIcon sx={{ fontSize: '12px !important' }} />}
          label={whatsappStatus?.isReady ? 'WA Ready' : whatsappStatus?.isConnected ? 'WA Connected' : 'WA Offline'}
          size="small"
          color={whatsappStatus?.isReady ? 'success' : whatsappStatus?.isConnected ? 'warning' : 'default'}
          sx={{ fontSize: 11, height: 22, '& .MuiChip-icon': { ml: 0.5 } }}
        />
      </Stack>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', flexShrink: 0 }} />

      {/* Nav items */}
      <List sx={{ flex: 1, overflowY: 'auto', px: 1, py: 0.5 }} disablePadding>
        {visibleEntries.map((entry) => {
          if (isGroup(entry)) {
            const groupActive = isGroupActive(entry);
            const open = openGroups.has(entry.label);
            return (
              <React.Fragment key={entry.label}>
                <ListItemButton
                  onClick={() => toggleGroup(entry.label)}
                  sx={itemSx(groupActive && !open)}
                >
                  <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                    {entry.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={entry.label}
                    primaryTypographyProps={{ fontSize: 13, fontWeight: groupActive ? 700 : 500 }}
                  />
                  {open ? (
                    <ExpandLessIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                  )}
                </ListItemButton>
                <Collapse in={open} timeout="auto" unmountOnExit>
                  <List disablePadding sx={{ pl: 1 }}>
                    {entry.children.map((child) => {
                      const active = isLeafActive(child.path);
                      return (
                        <ListItemButton
                          key={child.path}
                          onClick={() => handleNav(child.path)}
                          selected={active}
                          sx={{ ...itemSx(active), pl: 3 }}
                        >
                          <ListItemIcon sx={{ minWidth: 28, color: 'inherit' }}>
                            {child.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={child.label}
                            primaryTypographyProps={{ fontSize: 12, fontWeight: active ? 700 : 400 }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          }

          const active = isLeafActive(entry.path);
          const badge = entry.badge === 'rides' ? newRideCount : 0;
          return (
            <ListItemButton
              key={entry.path}
              onClick={() => handleNav(entry.path)}
              selected={active}
              sx={itemSx(active)}
            >
              <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                <Badge badgeContent={badge || undefined} color="error" max={99}>
                  {entry.icon}
                </Badge>
              </ListItemIcon>
              <ListItemText
                primary={entry.label}
                primaryTypographyProps={{ fontSize: 13, fontWeight: active ? 700 : 500 }}
              />
            </ListItemButton>
          );
        })}
      </List>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', flexShrink: 0 }} />

      {/* User info + logout */}
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 1.5, py: 1.5, flexShrink: 0 }}>
        <Avatar sx={{ width: 32, height: 32, bgcolor: '#ffc928', color: '#07182d', fontSize: 13, fontWeight: 800 }}>
          {(user?.name || '?').charAt(0).toUpperCase()}
        </Avatar>
        <Box minWidth={0} flex={1}>
          <Typography variant="caption" fontWeight={700} color="white" noWrap display="block">
            {user?.name}
          </Typography>
          <Typography variant="caption" color="rgba(255,255,255,0.5)" noWrap display="block" sx={{ fontSize: 10 }}>
            {user?.role}
          </Typography>
        </Box>
        <Tooltip title="Logout">
          <IconButton
            size="small"
            onClick={handleLogout}
            sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#ff5252' } }}
          >
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
};

const AdminLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { newRideCount } = useSocket();
  const [mobileOpen, setMobileOpen] = useState(false);

  const pageTitle = useMemo(() => getPageTitle(location.pathname), [location.pathname]);

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Desktop sidebar */}
      <Box
        component="aside"
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          bgcolor: '#061b31',
          color: 'white',
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100vh',
          borderRight: '1px solid rgba(255,255,255,0.08)',
          zIndex: 1200,
        }}
      >
        <SidebarContent />
      </Box>

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH, bgcolor: '#061b31', color: 'white', boxSizing: 'border-box' },
        }}
      >
        <SidebarContent onNavigate={() => setMobileOpen(false)} />
      </Drawer>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          ml: { xs: 0, md: `${SIDEBAR_WIDTH}px` },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
        }}
      >
        {/* Top header bar */}
        <Paper
          elevation={0}
          square
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1100,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Toolbar variant="dense" sx={{ minHeight: 52, px: { xs: 2, md: 3 } }}>
            <IconButton
              edge="start"
              sx={{ display: { xs: 'inline-flex', md: 'none' }, mr: 1 }}
              onClick={() => setMobileOpen(true)}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
              {pageTitle}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Badge badgeContent={newRideCount || undefined} color="error">
                <IconButton size="small">
                  <NotificationsIcon fontSize="small" />
                </IconButton>
              </Badge>
            </Stack>
          </Toolbar>
        </Paper>

        {/* Page content */}
        <Box sx={{ flex: 1, p: { xs: 2, md: 3 } }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;
